/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — docx-export.ts
   Word (.docx) export for financial statement packages.
   This file is a thin orchestration layer that composes the cover page,
   table of contents, statement sections, and notes into the final document.
   Implementation details live in:
     - docx-helpers.ts    (formatting helpers, style constants, AOA→Table)
     - docx-statements.ts (Balance Sheet / IS / Equity / Cash Flow data)
     - docx-notes.ts      (HTML notes → DOCX paragraphs/tables)
   ═══════════════════════════════════════════════════════════════════════ */

import {
  Document, Packer, Paragraph, TextRun,
  AlignmentType, BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';
import { meta, hasPriorData, extractYear } from './utils';
import { trackEvent } from './config';
import { stmtHeaderParagraphs, aoaToTable } from './docx-helpers';
import {
  buildBSData, buildISData, buildEquityData, buildCFData,
} from './docx-statements';
import { parseNotesToParagraphs } from './docx-notes';

// ─── Re-exports (barrel) ────────────────────────────────────────────────────
export {
  fmtAmt, BORDER_NONE, BORDER_BOTTOM, BORDER_DOUBLE_BOTTOM,
  stmtHeaderParagraphs, isTotal, isSectionHeader, buildTableRow,
  headerRow, aoaToTable,
} from './docx-helpers';
export {
  buildBSData, buildISData, buildEquityData, buildCFData,
} from './docx-statements';
export { parseNotesToParagraphs } from './docx-notes';

// ─── Main Export ────────────────────────────────────────────────────────────

export async function exportToWord(): Promise<void> {
  try {
    trackEvent('word_exports');
    const m = meta();
    const multi = hasPriorData();
    const priorYearOnly = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || m.priorPeriod || '';

    function bsPeriod(): string {
      if (multi && m.priorPeriod) return m.period + ' and ' + priorYearOnly;
      return m.period;
    }
    function flowPeriod(): string {
      const base = multi && m.priorPeriod ? m.period + ' and ' + priorYearOnly : m.period;
      return base && base !== 'Period' ? 'For the Year Ended ' + base : base;
    }

    // Determine equity title based on entity type
    const entityEl = document.getElementById('nq-entity-type') as HTMLSelectElement | null;
    const entityType = entityEl ? entityEl.value : '';
    const eqTitle = (entityType && (entityType.indexOf('LLC') >= 0 || entityType.indexOf('Partnership') >= 0))
      ? "Statement of Changes in Members' Equity"
      : "Statement of Stockholders' Equity";

    // Build statement data arrays (same as Excel/PDF exports)
    const bsData = buildBSData(multi);
    const isData = buildISData(multi);
    const eqData = buildEquityData(multi);
    const cfData = buildCFData(multi);

    // Statement names for TOC
    const tocItems = [
      'Balance Sheet',
      'Income Statement',
      eqTitle,
      'Statement of Cash Flows',
      'Notes to the Financial Statements',
    ];

    // ── Build sections ──

    // Cover page section
    const coverParagraphs: Paragraph[] = [
      new Paragraph({ spacing: { before: 3000 }, children: [] }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: m.company,
            bold: true,
            size: 56,
            font: 'Calibri',
            color: '1E293B',
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: 'Financial Statements',
            size: 32,
            font: 'Calibri',
            color: '64748B',
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        border: {
          bottom: { style: BorderStyle.SINGLE, size: 2, color: '2563EB', space: 1 },
        },
        children: [],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 200, after: 100 },
        children: [
          new TextRun({
            text: bsPeriod(),
            size: 24,
            font: 'Calibri',
            color: '1E293B',
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 400 },
        children: [
          new TextRun({
            text: 'Prepared with NoteFlow',
            size: 20,
            font: 'Calibri',
            color: '94A3B8',
            italics: true,
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: 'Generated: ' + new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            }),
            size: 18,
            font: 'Calibri',
            color: '94A3B8',
          }),
        ],
      }),
    ];

    // Table of Contents section
    const tocParagraphs: Paragraph[] = [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 400 },
        children: [
          new TextRun({
            text: 'Table of Contents',
            bold: true,
            size: 36,
            font: 'Calibri',
            color: '1E293B',
          }),
        ],
      }),
    ];

    tocItems.forEach((title, idx) => {
      tocParagraphs.push(
        new Paragraph({
          spacing: { after: 200 },
          tabStops: [{ type: 'right', position: 9000, leader: 'dot' }],
          children: [
            new TextRun({
              text: title,
              size: 22,
              font: 'Calibri',
              color: '1E293B',
            }),
            new TextRun({
              text: '\t' + String(idx + 3),
              size: 22,
              font: 'Calibri',
              color: '64748B',
            }),
          ],
        }),
      );
    });

    // Balance Sheet section
    const bsParagraphs: Paragraph[] = [
      ...stmtHeaderParagraphs(m.company, 'Balance Sheet', bsPeriod()),
    ];

    // Income Statement section
    const isParagraphs: Paragraph[] = [
      ...stmtHeaderParagraphs(m.company, 'Income Statement', flowPeriod()),
    ];

    // Equity section
    const eqParagraphs: Paragraph[] = [
      ...stmtHeaderParagraphs(m.company, eqTitle, flowPeriod()),
    ];

    // Cash Flow section
    const cfParagraphs: Paragraph[] = [
      ...stmtHeaderParagraphs(m.company, 'Statement of Cash Flows', flowPeriod()),
    ];

    // Notes section
    const notesParagraphs: Paragraph[] = [
      ...stmtHeaderParagraphs(m.company, 'Notes to the Financial Statements', flowPeriod()),
      ...parseNotesToParagraphs(),
    ];

    // Build the document with sections
    const doc = new Document({
      creator: 'NoteFlow',
      title: m.company + ' - Financial Statements',
      description: 'Financial Statement Package generated by NoteFlow',
      styles: {
        default: {
          document: {
            run: { font: 'Calibri', size: 20 },
          },
        },
      },
      sections: [
        // Cover page
        {
          properties: {},
          children: coverParagraphs,
        },
        // Table of Contents
        {
          properties: {
            page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } },
          },
          children: tocParagraphs,
        },
        // Balance Sheet
        {
          properties: {
            page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } },
          },
          children: [
            ...bsParagraphs,
            aoaToTable(bsData, multi),
          ],
        },
        // Income Statement
        {
          properties: {
            page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } },
          },
          children: [
            ...isParagraphs,
            aoaToTable(isData, multi),
          ],
        },
        // Equity
        {
          properties: {
            page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } },
          },
          children: [
            ...eqParagraphs,
            aoaToTable(eqData, multi),
          ],
        },
        // Cash Flow
        {
          properties: {
            page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } },
          },
          children: [
            ...cfParagraphs,
            aoaToTable(cfData, multi),
          ],
        },
        // Notes
        {
          properties: {
            page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } },
          },
          children: notesParagraphs,
        },
      ],
    });

    // Generate and save
    const blob = await Packer.toBlob(doc);
    const filename = (m.company || 'Financial_Statements')
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .replace(/\s+/g, '_') + '.docx';
    saveAs(blob, filename);
  } catch (err) {
    console.error('Word export failed:', err);
    alert('Word export failed. Please try again.');
  }
}
